/**
 * Fractional order keys with jitter.
 *
 * Siblings are sorted by `order` (plain code-unit string comparison, never localeCompare; in
 * Postgres the column needs COLLATE "C"). A move or insert changes exactly one node's key; siblings
 * are never renumbered.
 *
 * Why jitter: plain midpoint keys are deterministic, so two devices inserting at the same spot
 * get the same key, and a key freed by a move can be handed out again and collide when the move
 * is undone. A short random suffix makes both practically impossible (1 in 14.8M per pair with
 * the same neighbours). Ties that still happen are broken by node id in `compareSiblings`.
 */
import { generateKeyBetween, generateNKeysBetween } from 'fractional-indexing'
import type { Rand } from './rng.ts'

const DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
const JITTER_LEN = 4

export function compareKeys(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}

/** Total order for siblings: key first, id as the tie-break. */
export function compareSiblings(aKey: string, aId: string, bKey: string, bId: string): number {
  return compareKeys(aKey, bKey) || compareKeys(aId, bId)
}

function suffix(rand: Rand): string {
  let s = ''
  for (let i = 0; i < JITTER_LEN - 1; i++) s += DIGITS[Math.floor(rand() * 62)]!
  // A key must not end with the zero digit, or nothing could be inserted before it.
  return s + DIGITS[1 + Math.floor(rand() * 61)]!
}

function jitter(key: string, hi: string | null, rand: Rand): string {
  let k = key
  for (let attempt = 0; attempt < 8; attempt++) {
    const c = k + suffix(rand)
    // c > k > lo always holds; only the upper bound can fail (when k is a prefix of hi).
    if (hi === null || c < hi) return c
    k = generateKeyBetween(k, hi)
  }
  return k
}

/** `n` jittered keys strictly between `lo` and `hi` (null = open end), ascending. */
export function keysBetween(lo: string | null, hi: string | null, n: number, rand: Rand): string[] {
  if (n <= 0) return []
  if (lo !== null && hi !== null && !(lo < hi)) throw new Error(`keysBetween: ${lo} !< ${hi}`)
  const base = n === 1 ? [generateKeyBetween(lo, hi)] : generateNKeysBetween(lo, hi, n)
  const out: string[] = []
  for (let i = 0; i < n; i++) {
    const upper = i + 1 < n ? base[i + 1]! : hi
    out.push(jitter(base[i]!, upper, rand))
  }
  return out
}
